import { ExtendedLandingPage } from '@/context/analytics-context/types'
import { LandingPage } from '@prisma/client'
import juice from 'juice'
import { twi } from 'tw-to-css'
import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

const convertTailwindToInline = (html: string | undefined) => {
  return html?.replace(/class="([^"]*)"/g, (match, classNames) => {
    const inlineStyles = twi(classNames)
    return `style="${inlineStyles}"`
  })
}

const extractStyles = (
  css: string | undefined,
  type: 'id' | 'class'
): Record<string, string> => {
  if (!css) return {}

  const styles: Record<string, string> = {}

  // Regular expression for extracting styles based on 'id' or 'class' selectors
  const regex =
    type === 'id'
      ? /#([a-zA-Z0-9_-]+)\s*{([^}]*)}/g
      : /\.([a-zA-Z0-9_-]+)\s*{([^}]*)}/g

  let match
  while ((match = regex.exec(css)) !== null) {
    const name = match[1].trim()
    const rules = match[2].trim().replace(/\n/g, '').replace(/\s+/g, ' ')
    styles[name] = rules
  }

  return styles
}

export const exportEmailHtml = (
  html: string | undefined,
  css: string | undefined
) => {
  const inlineHTML = convertTailwindToInline(html)
  let newInlineHTML = inlineHTML

  const idStyles = extractStyles(css, 'id')
  const classStyles = extractStyles(html, 'class')  

  let parsedHtml = juice(newInlineHTML || '')

  return juice(`
  <!DOCTYPE html PUBLIC "-//W3C//DTD XHTML 1.0 Transitional//EN" "http://www.w3.org/TR/xhtml1/DTD/xhtml1-transitional.dtd">
  <html xmlns="http://www.w3.org/1999/xhtml">
    <head>
      <meta http-equiv="Content-Type" content="text/html; charset=UTF-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0"/>
      <meta name="color-scheme" content="">
      <meta name="supported-color-schemes" content="">
      <style type="text/css">
                  /* Base styles */
          body {
            margin: 0 !important;
            padding: 0 !important;
            width: 100% !important;
            -webkit-text-size-adjust: 100% !important;
            -ms-text-size-adjust: 100% !important;
            -webkit-font-smoothing: antialiased !important;
          }
          
          /* Client-specific styles */
          body, table, td, a { -webkit-text-size-adjust: 100%; -ms-text-size-adjust: 100%; }
          table, td { mso-table-lspace: 0pt; mso-table-rspace: 0pt; }

          /* Reset styles */
          table { 
            border-collapse: separate !important; 
            border-spacing: 0 !important;
            table-layout: fixed !important; 
            width: 100% !important;
            max-width: 600px !important;
            margin: 0 auto !important;
          }
          
          body { height: 100% !important; margin: 0 !important; padding: 0 !important; width: 100% !important; }

          /* iOS blue links */
          a[x-apple-data-detectors] {
            color: inherit !important;
            text-decoration: none !important;
            font-size: inherit !important;
            font-family: inherit !important;
            font-weight: inherit !important;
            line-height: inherit !important;
          }

          /* Table cell styling */
          td, th {
            padding: 5px !important;
            vertical-align: top !important;
            position: relative !important;
            word-wrap: break-word !important;
            overflow-wrap: break-word !important;
          }
          
          /* Fix nested tables */
          table table {
            width: 100% !important;
            max-width: 100% !important;
          }
          
          /* Fix row display */
          [data-gjs-type="row"],
          .gjs-row {
            clear: both !important;
            display: table !important;
            width: 100% !important;
            table-layout: fixed !important;
            margin-left: 0 !important;
            margin-right: 0 !important;
          }
          
          /* Fix cell display */
          [data-gjs-type="cell"],
          .gjs-cell {
            display: table-cell !important;
            vertical-align: top !important;
            float: none !important;
            padding: 0 5px !important;
            position: relative !important;
          }
          
          /* Fix text content */
          [data-gjs-type="text"] {
            word-wrap: break-word !important;
            overflow-wrap: break-word !important;
            position: static !important;
            display: block !important;
            clear: both !important;
          }
          
          /* Image styling */
          img {
            color: black !important;
            min-width: 0 !important;
            box-sizing: border-box !important;
            max-width: 100% !important;
            height: auto !important;
            -ms-interpolation-mode: bicubic !important;
            display: block !important;
          }
          
          /* Mobile responsiveness */
          @media screen and (max-width: 600px) {
            table, td {
              width: 100% !important;
              max-width: 100% !important;
            }
            
            [data-gjs-type="cell"],
            .gjs-cell {
              display: block !important;
              width: 100% !important;
              padding: 5px !important;
            }
            
            [data-gjs-type="row"],
            .gjs-row {
              display: block !important;
            }
            
            img {
              height: auto !important;
            }
          }
          
          /* General responsive styles */
          html, body, * { min-width: 0 !important; box-sizing: border-box !important; }
            ${css}
          </style>
        </head>
          ${parsedHtml}
      </html>
  `)
}

export const convertInternalCssToInline = (html: string, css: string) => {
  // Convert internal CSS to inline styles
  const inlinedHtml = juice.inlineContent(html, css)
  return inlinedHtml
}

export const clearSelectedEmails = () => {
  sessionStorage.removeItem('selectedLeads')
}

export const getLocalStorage = (key: string) => {
  if (typeof window !== 'undefined') {
    return localStorage.getItem(key)
  }
  return null
}

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export const upsertObjectImmutable = (
  array: ExtendedLandingPage[],
  newObj: LandingPage,
  key: string
): ExtendedLandingPage[] => {
  const existingIndex = array.findIndex(
    (obj: ExtendedLandingPage) =>
      obj[key as keyof ExtendedLandingPage] === newObj[key as keyof LandingPage]
  )

  if (existingIndex !== -1) {
    // If object exists, update it immutably
    return array.map((obj, index) =>
      index === existingIndex ? { ...obj, ...newObj } : obj
    )
  } else {
    // If object does not exist, add it immutably
    return [...array, newObj]
  }
}

export const getLandingPageFromLocalStorage =
  (): ExtendedLandingPage | null => {
    if (typeof window !== 'undefined') {
      const storedData = localStorage.getItem('builder-content')
      return storedData ? JSON.parse(storedData) : null
    }
    return null
  }

export const handleApiError = (
  error: unknown,
  defaultMessage = 'Something went wrong'
) => {
  if (
    typeof error === 'object' &&
    error !== null &&
    'response' in error &&
    error.response &&
    typeof error.response === 'object' &&
    'data' in error.response &&
    error.response.data &&
    typeof error.response.data === 'object' &&
    'message' in error.response.data &&
    typeof error.response.data.message === 'string'
  ) {
    throw new Error(error.response.data.message)
  }  else if (
    typeof error === 'object' &&
    error !== null &&
    'response' in error &&
    error.response &&
    typeof error.response === 'object' &&
    'data' in error.response &&
    error.response.data &&
    typeof error.response.data === 'object' &&
    'detail' in error.response.data &&
    typeof error.response.data.detail === 'string'
  ) {  
    throw new Error(error.response.data.detail)
  } else if (error instanceof Error) {
    throw new Error(error.message)
  } else {
    throw new Error(defaultMessage)
  }
}

export const formatPercentage = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'percent',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value / 100)
}
